import React, { useEffect } from "react";
import { useNavigate } from "react-router";
import { ExternalLink, Globe, Smartphone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { visitorAuthService } from "../services/visitorAuthService";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { maxMiniAppOpenUrl } from "../utils/publicEntry";
import { useAppStore } from "../store/useAppStore";

/**
 * Intermediate landing: choose MAX mini-app vs browser login.
 * Canonical URL: https://app.mvk.ru/start
 */
export function EntryChooserPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { onboardingComplete, selectedEventId } = useAppStore();
  const insideMax = visitorAuthService.isMaxRuntime();

  useEffect(() => {
    if (visitorAuthService.isLoggedIn()) {
      navigate(
        onboardingComplete && selectedEventId ? "/home" : "/onboarding",
        { replace: true },
      );
      return;
    }
    // Already inside MAX (SDK or max.* host) — skip chooser, go to mini-app login.
    // isMaxRuntime covers late initData: host is enough to avoid flashing «Войти через MAX».
    if (insideMax) {
      navigate("/login", { replace: true });
    }
  }, [navigate, onboardingComplete, selectedEventId, insideMax]);

  // Avoid flashing «Войти через MAX» while redirecting into the mini-app login.
  if (insideMax && !visitorAuthService.isLoggedIn()) {
    return null;
  }

  const openMax = () => {
    const url = maxMiniAppOpenUrl();
    window.location.assign(url);
  };

  const openBrowser = () => {
    navigate("/login");
  };

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-[#0b1020] text-white">
      {/* Atmosphere */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 90% 55% at 50% -10%, rgba(99,102,241,0.45), transparent 55%)," +
            "radial-gradient(ellipse 70% 45% at 100% 80%, rgba(124,58,237,0.22), transparent 50%)," +
            "radial-gradient(ellipse 55% 40% at 0% 70%, rgba(56,189,248,0.12), transparent 45%)," +
            "linear-gradient(180deg, #0b1020 0%, #111827 48%, #0b1020 100%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px)," +
            "linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse 80% 70% at 50% 30%, black, transparent)",
        }}
      />

      <div className="relative z-10 mx-auto flex min-h-[100dvh] w-full max-w-lg flex-col px-5 pb-8 pt-[max(1rem,env(safe-area-inset-top))]">
        <div className="absolute right-5 top-[max(1rem,env(safe-area-inset-top))] z-20 pt-3">
          <LanguageSwitcher className="border-white/15 bg-white/10 backdrop-blur-md [&_button]:text-white/70 [&_button[aria-pressed=true]]:bg-white [&_button[aria-pressed=true]]:text-indigo-700" />
        </div>

        {/* Brand + CTAs as one centered block */}
        <div className="flex flex-1 flex-col items-center justify-center gap-8 px-1 py-10">
          <div className="flex w-full flex-col items-center text-center">
            <img
              src="/icons/app-icon.png?v=20260916"
              alt="MVK One"
              width={72}
              height={72}
              className="mb-5 h-[72px] w-[72px] rounded-[18px] shadow-[0_0_40px_rgba(99,102,241,0.35)] ring-1 ring-white/20"
            />

            <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.28em] text-indigo-200/90">
              {t("visitor.entry_tagline", {
                defaultValue: "Цифровой помощник выставки",
              })}
            </p>

            <h1 className="mb-4 text-[clamp(2.75rem,12vw,3.75rem)] font-semibold leading-[0.95] tracking-[-0.04em] text-white">
              {t("visitor.entry_brand", { defaultValue: "MVK One" })}
            </h1>

            <p className="mb-8 max-w-[20rem] text-[15px] leading-relaxed text-white/65">
              {t("visitor.entry_hello", {
                defaultValue:
                  "Собирайте и сохраняйте деловые контакты, информацию о товарах, заметки и другую полезную информацию о выставке — всё в одном приложении.",
              })}
            </p>
          </div>

          <div className="w-full space-y-3">
            <p className="mb-1 text-center text-[12px] uppercase tracking-[0.18em] text-white/40">
              {t("visitor.entry_choose", { defaultValue: "Выберите способ входа" })}
            </p>

            <button
              type="button"
              onClick={openMax}
              className="group w-full cursor-pointer rounded-2xl bg-indigo-500 px-4 py-4 text-left text-white shadow-[0_12px_40px_rgba(79,70,229,0.45)] transition-all hover:bg-indigo-400 hover:shadow-[0_16px_48px_rgba(79,70,229,0.55)] active:scale-[0.99]"
            >
              <span className="flex items-center gap-3">
                <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
                  <Smartphone size={20} />
                </span>
                <span className="flex-1">
                  <span className="block text-[15px] font-medium">
                    {t("visitor.entry_max", { defaultValue: "Войти через MAX" })}
                  </span>
                  <span className="mt-0.5 block text-[12px] text-indigo-100/90">
                    {t("visitor.max_fast_login", {
                      defaultValue: "быстрый вход по номеру телефона",
                    })}
                  </span>
                </span>
                <ExternalLink size={16} className="opacity-70 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </button>

            <button
              type="button"
              onClick={openBrowser}
              className="w-full cursor-pointer rounded-2xl border border-white/15 bg-white/8 px-4 py-4 text-left backdrop-blur-md transition-all hover:border-white/25 hover:bg-white/12 active:scale-[0.99]"
            >
              <span className="flex items-center gap-3">
                <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/10">
                  <Globe size={20} className="text-white/90" />
                </span>
                <span className="flex-1">
                  <span className="block text-[15px] font-medium text-white">
                    {t("visitor.entry_browser", {
                      defaultValue: "Войти через браузер",
                    })}
                  </span>
                  <span className="mt-0.5 block text-[12px] text-white/50">
                    {t("visitor.browser_login_hint", {
                      defaultValue: "вход по номеру телефона и штрихкоду бейджа",
                    })}
                  </span>
                </span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
